import { LCWorkTriple } from "@libry-content/integrations";
import { ManifestationStatusForUserType } from "../../manifestations/useManifestationStatusForUser";
import { FormEventHandler, SetStateAction, useState } from "react";
import { useTranslation } from "../../../../utils/hooks/useTranslation";
import { DialogLC } from "../../../../components/ModalContainer";
import { AlertBS, ButtonBS, colors, Heading, HStack, RadioCard, Text, VStack } from "@biblioteksentralen/react";
import { useWorkViewContext } from "../../works/WorkViewContextProvider";
import { ItemCoverImage } from "../../ItemCoverImage";
import { isNumber } from "radash";
import { getDotseparated } from "../../getDotseparated";
import { HoldingText } from "../../HoldingText";

export const PickPartedOrWholeManifestationModal = ({
  partedTriples,
  nonPartedTriples,
  manifestationStatusForUser,
  setChosenPartedWorkTriple,
  isOpen,
  handleClose,
}: {
  partedTriples: LCWorkTriple[];
  nonPartedTriples: LCWorkTriple[];
  manifestationStatusForUser: ManifestationStatusForUserType;
  setChosenPartedWorkTriple: (value: SetStateAction<LCWorkTriple | undefined>) => void;
  isOpen: boolean;
  handleClose: () => void;
}) => {
  const { t } = useTranslation();
  const [chosenWorkTriple, setChosenWorkTriple] = useState<LCWorkTriple | undefined>(
    [...nonPartedTriples, ...partedTriples].find((workTriple) =>
      isWorkTripleReservable(workTriple, manifestationStatusForUser)
    )
  );

  const handleSubmit: FormEventHandler = (event) => {
    event.preventDefault();
    if (!chosenWorkTriple) return;
    setChosenPartedWorkTriple(chosenWorkTriple);
  };

  return (
    <form onSubmit={handleSubmit}>
      <DialogLC
        heading={t("Reserver")}
        modalContentProps={{ maxH: "40rem" }}
        footer={
          <HStack justifyContent="space-between" width="100%">
            <ButtonBS variant="outline" onClick={handleClose}>
              {t("Avbryt")}
            </ButtonBS>
            <ButtonBS
              type="submit"
              disabled={!chosenWorkTriple || !isWorkTripleReservable(chosenWorkTriple, manifestationStatusForUser)}
              onClick={handleSubmit}
              value={chosenWorkTriple?.manifestationId}
            >
              {t("Velg")}
            </ButtonBS>
          </HStack>
        }
        open={isOpen}
        onOpenChange={() => {
          setChosenWorkTriple(
            [...nonPartedTriples, ...partedTriples].find((workTriple) =>
              isWorkTripleReservable(workTriple, manifestationStatusForUser)
            )
          );
          handleClose();
        }}
      >
        <RadioCard.Root defaultValue={chosenWorkTriple?.manifestationId} width="100%">
          {nonPartedTriples.length > 0 && <RadioCard.Label>{t("Utgaver")}</RadioCard.Label>}{" "}
          <VStack width="100%" gap="0.75rem" alignItems="flex-start">
            {nonPartedTriples.map((workTriple) => (
              <ManifestationRadioOption
                key={workTriple.manifestationId}
                workTriple={workTriple}
                chosenManifestationId={chosenWorkTriple?.manifestationId}
                setChosenManifestation={setChosenWorkTriple}
                manifestationStatusForUser={manifestationStatusForUser}
              />
            ))}
            {partedTriples.length > 0 && (
              <RadioCard.Label>
                <Text fontSize="md" marginBottom="0.25rem" fontWeight="semibold">
                  {t("Utgaver i flere deler")}
                </Text>
              </RadioCard.Label>
            )}
            {partedTriples.map((workTriple) => (
              <ManifestationRadioOption
                key={workTriple.manifestationId}
                workTriple={workTriple}
                chosenManifestationId={chosenWorkTriple?.manifestationId}
                setChosenManifestation={setChosenWorkTriple}
                manifestationStatusForUser={manifestationStatusForUser}
              />
            ))}
          </VStack>
        </RadioCard.Root>
      </DialogLC>
    </form>
  );
};

const ManifestationRadioOption = (props: {
  workTriple: LCWorkTriple;
  chosenManifestationId: string | undefined;
  setChosenManifestation: (workTriple: LCWorkTriple) => void;
  manifestationStatusForUser: ManifestationStatusForUserType;
}) => {
  const { t } = useTranslation();
  const { holdingsData } = useWorkViewContext();
  const workTriple = props.workTriple;
  const partNumber = workTriple.partNumber;
  const manifestationCanBeReserved = isWorkTripleReservable(workTriple, props.manifestationStatusForUser);

  return (
    <RadioCard.Item
      key={workTriple.manifestationId}
      value={workTriple.manifestationId}
      onChange={() => {
        props.setChosenManifestation(workTriple);
      }}
      disabled={!manifestationCanBeReserved}
      width="100%"
    >
      <RadioCard.ItemHiddenInput />
      <RadioCard.ItemControl>
        <RadioCard.ItemContent>
          <HStack justifyContent="space-between" width="100%">
            <HStack>
              <ItemCoverImage
                alignSelf="center"
                item={workTriple}
                height="fit-content"
                minWidth="4rem"
                maxWidth="4rem"
                {...(!manifestationCanBeReserved ? { opacity: "60%" } : {})}
              />
              <VStack alignItems="flex-start" justifyContent="space-between" gap="0.5rem">
                <VStack alignItems="flex-start" justifyContent="center" gap="-0.5rem">
                  <RadioCard.ItemText>
                    <Heading as="h3" fontSize="xl">
                      {workTriple.getFormattedManifestationTitle({ skipSubtitle: true })}
                    </Heading>
                  </RadioCard.ItemText>

                  {partNumber && (
                    <RadioCard.ItemDescription>
                      <Text fontStyle="italic" fontSize="md">
                        {isNumber(Number.parseInt(partNumber[0] ?? "")) ? `${t("Del")} ${partNumber}` : partNumber}
                      </Text>
                    </RadioCard.ItemDescription>
                  )}
                  <Text fontSize="sm">
                    {getDotseparated([
                      ...workTriple.publicationStatements.flatMap(
                        (publicationStatement) => publicationStatement.imprint.name
                      ),
                      workTriple.publicationYear,
                    ])}
                  </Text>
                </VStack>
                {!manifestationCanBeReserved && (
                  <AlertBS status="info" padding="0.25rem" paddingRight="0.5rem" gap="0.25rem" fontSize="smaller">
                    <Text fontSize="sm" color={colors.black}>
                      {t("Allerede reservert")}
                    </Text>
                  </AlertBS>
                )}
                <HoldingText workTriple={workTriple} holdings={holdingsData?.holdings} />
              </VStack>
            </HStack>
            <RadioCard.ItemIndicator />
          </HStack>
        </RadioCard.ItemContent>
      </RadioCard.ItemControl>
    </RadioCard.Item>
  );
};

const isWorkTripleReservable = (workTriple: LCWorkTriple, manifestationStatusForUser: ManifestationStatusForUserType) =>
  !(
    manifestationStatusForUser.loanedTriples?.find(
      ({ manifestationId }) => workTriple.manifestationId === manifestationId
    ) ??
    manifestationStatusForUser.reservedTriples?.find(
      ({ manifestationId }) => workTriple.manifestationId === manifestationId
    )
  );
